import React, { useEffect, useMemo } from 'react';

// DEPENDENCIES
import clsx from 'clsx';
import { useFormikContext } from 'formik';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';

// GLOBAL VARIABLES
import { FORM_TABLE } from 'defaults.js';

// FORM COMPONENTS
import Col from 'core/form/parts/Col';

// LOCAL COMPONENTS
import TableHeader from './parts/TableHeader';
import TableBody from './parts/TableBody';
import AddButton from './tools/AddButton';

//STYLES
import styles from './table.module.scss';

// MAIN COMPONENT
const Table = ({
  title,
  useEmptyMessage = FORM_TABLE.useEmptyMessage,
  buttonBefore = FORM_TABLE.buttonBefore,
  hideHeader = false,
  className,
  showAdd = true,
  ...props
}) => {
  
  // PROPS
  const {
    name,
    itemLabel = 'Item',
    defaultValue,
    min = FORM_TABLE.min || 0,
    max = FORM_TABLE.max,
    disabled
  } = props;

  // FORMIK CONTEXT
  const { values, setFieldValue } = useFormikContext() || {};

  // MEMOS
  const value = useMemo(
    () => cloneDeep(get(values, name)) || [],
    [name, values]
  )
  const hideTable = useMemo(() => value?.every(e => e?.isDeleted === true), [value]);
  const allowDelete = useMemo(() => !disabled && min < value.length, [disabled, min, value]);

  // EFFECTS
  useEffect(
    () => {
      const neededItems = min - value.length;
      if (neededItems <= 0) return;
      const newValue = cloneDeep(value);
      Array(neededItems).fill().forEach(() => newValue.push(defaultValue));
      setFieldValue(name, newValue);
    },
    [name, value, defaultValue, min, setFieldValue]
  )
  
  // RENDER
  return (
    <Col className={clsx(styles.container, className)} cols={{xs: 24}}>
      {!!title &&
        <h4 className={styles.title}>{title}</h4>
      }
      {showAdd && (!max || value.length <= max) &&
        <div className={clsx(
          styles.addButton,
          buttonBefore && styles.buttonBefore
        )}>
          <AddButton {...props} />
        </div>
      }
      {!value.length ? !useEmptyMessage ? null : <span className={styles.message}>No {itemLabel}s</span> : (
        <div className={clsx(styles.table, hideTable && 'd-none')}>
          {hideTable ? null : !hideHeader && <TableHeader allowDelete={allowDelete} {...props} />}
          <TableBody allowDelete={allowDelete} {...props} />
        </div>
      )}
    </Col>
  )
}

// EXPORT
export default Table
